/**
 * Sidebar Component
 *
 * A collapsible sidebar navigation component with icons and labels.
 * Supports collapsed state with tooltips on hover.
 *
 * Validates: Requirements 2.1, 2.2, 2.3
 * - Requirement 2.1: THE Console SHALL display a collapsible sidebar navigation
 *   with icons and labels.
 * - Requirement 2.2: WHEN the sidebar is collapsed, THE Console SHALL show only
 *   icons with tooltips on hover.
 * - Requirement 2.3: THE Console SHALL highlight the current active section in
 *   the navigation.
 */

import React, { forwardRef } from 'react';
import { tokens } from '../tokens';
import { Tooltip } from './Tooltip';

export interface SidebarItem {
  /** Unique identifier for the item */
  id: string;
  /** Icon element to display */
  icon: React.ReactNode;
  /** Label text for the item */
  label: string;
  /** Navigation href (optional) */
  href?: string;
  /** Whether this item is currently active */
  active?: boolean;
}

export interface SidebarProps {
  /** Whether the sidebar is collapsed */
  collapsed: boolean;
  /** Callback when toggle button is clicked */
  onToggle: () => void;
  /** Navigation items to display */
  items: SidebarItem[];
  /** Callback when an item is clicked */
  onItemClick: (item: SidebarItem) => void;
  /** Optional header content (logo, title, etc.) */
  header?: React.ReactNode;
  /** Optional footer content */
  footer?: React.ReactNode;
}

export interface SidebarItemProps {
  /** The sidebar item data */
  item: SidebarItem;
  /** Whether the sidebar is collapsed */
  collapsed: boolean;
  /** Callback when the item is clicked */
  onClick: (item: SidebarItem) => void;
}

/** Sidebar width constants */
const SIDEBAR_WIDTH_EXPANDED = 240;
const SIDEBAR_WIDTH_COLLAPSED = 64;

/**
 * Chevron icon for toggle button
 */
function ChevronIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        transform: direction === 'left' ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: `transform ${tokens.transitions.normal}`,
      }}
    >
      <path
        d="M7.5 15L12.5 10L7.5 5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Individual sidebar navigation item component.
 *
 * Features:
 * - Icon and label display
 * - Active state highlighting with primary color
 * - Hover state with subtle background change
 * - When collapsed: shows only icon with Tooltip on hover
 *
 * @example
 * ```tsx
 * <SidebarItemComponent
 *   item={{ id: 'home', icon: <HomeIcon />, label: 'Home', active: true }}
 *   collapsed={false}
 *   onClick={(item) => navigate(item.href)}
 * />
 * ```
 */
export const SidebarItemComponent = forwardRef<HTMLButtonElement, SidebarItemProps>(
  function SidebarItemComponent({ item, collapsed, onClick }, ref) {
    const [isHovered, setIsHovered] = React.useState(false);

    const baseStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing[3],
      width: '100%',
      padding: collapsed ? tokens.spacing[3] : `${tokens.spacing[3]} ${tokens.spacing[4]}`,
      border: 'none',
      borderRadius: tokens.borderRadius.md,
      cursor: 'pointer',
      fontFamily: tokens.typography.fontFamily.sans,
      fontSize: tokens.typography.fontSize.sm,
      fontWeight: item.active
        ? tokens.typography.fontWeight.semibold
        : tokens.typography.fontWeight.medium,
      lineHeight: tokens.typography.lineHeight.normal,
      textAlign: 'left',
      textDecoration: 'none',
      transition: `all ${tokens.transitions.normal}`,
      justifyContent: collapsed ? 'center' : 'flex-start',
      // Active state: primary background with white text
      backgroundColor: item.active
        ? tokens.colors.primary[600]
        : isHovered
          ? tokens.colors.neutral[100]
          : 'transparent',
      color: item.active
        ? '#ffffff'
        : tokens.colors.neutral[700],
    };

    const iconStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      width: '20px',
      height: '20px',
    };

    const labelStyles: React.CSSProperties = {
      opacity: collapsed ? 0 : 1,
      width: collapsed ? 0 : 'auto',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      transition: `opacity ${tokens.transitions.normal}, width ${tokens.transitions.normal}`,
    };

    const handleClick = () => {
      onClick(item);
    };

    const buttonContent = (
      <button
        ref={ref}
        type="button"
        style={baseStyles}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        aria-current={item.active ? 'page' : undefined}
      >
        <span style={iconStyles}>{item.icon}</span>
        {!collapsed && <span style={labelStyles}>{item.label}</span>}
      </button>
    );

    // When collapsed, wrap with Tooltip to show label on hover
    if (collapsed) {
      return (
        <Tooltip content={item.label} position="right" showDelay={100}>
          {buttonContent}
        </Tooltip>
      );
    }

    return buttonContent;
  }
);

/**
 * Collapsible sidebar navigation component.
 *
 * Features:
 * - Collapsible state (expanded: 240px, collapsed: 64px)
 * - Toggle button to collapse/expand
 * - Smooth transition animation (200ms)
 * - Support for navigation items with icons and labels
 * - Active item highlighting with primary color
 * - Tooltips on hover when collapsed
 *
 * Color contrast meets WCAG 2.1 AA standards:
 * - Active item: white text on primary-600 background (contrast ratio > 4.5:1)
 * - Inactive item: neutral-700 text on transparent/neutral-100 background (contrast ratio > 4.5:1)
 *
 * @example
 * ```tsx
 * const [collapsed, setCollapsed] = useState(false);
 * const items = [
 *   { id: 'dashboard', icon: <DashboardIcon />, label: 'Dashboard', active: true },
 *   { id: 'agents', icon: <AgentsIcon />, label: 'Agents' },
 *   { id: 'settings', icon: <SettingsIcon />, label: 'Settings' },
 * ];
 *
 * <Sidebar
 *   collapsed={collapsed}
 *   onToggle={() => setCollapsed(!collapsed)}
 *   items={items}
 *   onItemClick={(item) => navigate(`/${item.id}`)}
 * />
 * ```
 */
export const Sidebar = forwardRef<HTMLElement, SidebarProps>(
  function Sidebar(
    { collapsed, onToggle, items, onItemClick, header, footer },
    ref
  ) {
    const [toggleHovered, setToggleHovered] = React.useState(false);

    const sidebarStyles: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      width: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED,
      height: '100%',
      backgroundColor: tokens.colors.neutral[50],
      borderRight: `1px solid ${tokens.colors.neutral[200]}`,
      transition: `width ${tokens.transitions.normal}`,
      overflow: 'hidden',
    };

    const headerStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: collapsed ? 'center' : 'space-between',
      padding: tokens.spacing[4],
      borderBottom: `1px solid ${tokens.colors.neutral[200]}`,
      minHeight: '64px',
    };

    const toggleButtonStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '32px',
      height: '32px',
      padding: 0,
      border: `1px solid ${tokens.colors.neutral[200]}`,
      borderRadius: tokens.borderRadius.md,
      backgroundColor: toggleHovered
        ? tokens.colors.neutral[100]
        : tokens.colors.neutral[50],
      color: tokens.colors.neutral[600],
      cursor: 'pointer',
      transition: `all ${tokens.transitions.fast}`,
      flexShrink: 0,
    };

    const navStyles: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing[1],
      padding: tokens.spacing[3],
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
    };

    const footerStyles: React.CSSProperties = {
      padding: tokens.spacing[3],
      borderTop: `1px solid ${tokens.colors.neutral[200]}`,
    };

    return (
      <aside ref={ref} style={sidebarStyles} aria-label="Main navigation">
        {/* Header section */}
        <div style={headerStyles}>
          {header && !collapsed && (
            <div style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
              {header}
            </div>
          )}
          <Tooltip
            content={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            position="right"
            showDelay={300}
          >
            <button
              type="button"
              style={toggleButtonStyles}
              onClick={onToggle}
              onMouseEnter={() => setToggleHovered(true)}
              onMouseLeave={() => setToggleHovered(false)}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!collapsed}
            >
              <ChevronIcon direction={collapsed ? 'right' : 'left'} />
            </button>
          </Tooltip>
        </div>

        {/* Navigation items */}
        <nav style={navStyles} role="navigation">
          {items.map((item) => (
            <SidebarItemComponent
              key={item.id}
              item={item}
              collapsed={collapsed}
              onClick={onItemClick}
            />
          ))}
        </nav>

        {/* Footer section */}
        {footer && (
          <div style={footerStyles}>
            {collapsed ? null : footer}
          </div>
        )}
      </aside>
    );
  }
);

export default Sidebar;
